/** Read-only selectors over a `PlayerObservation`.
 *
 * Every one is pure and total: an id that is not in the observation returns
 * null rather than throwing, because the observation is fog-masked and a panel
 * that crashes when an enemy hero walks out of sight is a bug that only shows
 * up in a real game.
 *
 * Ordering is by id, never by array position: `observe()` walks `entityKeys`,
 * which is already sorted, but the panels must not silently depend on that.
 */

import { TERRAINS } from "@game/core";
import type { HoverTarget } from "./types.js";
import type {
  ArmyState, HeroId, ObjectId, ObservedHero, ObservedObject, ObservedTown,
  PlayerObservation, TerrainKind, TownId,
} from "@game/core";

const byId = <T extends { readonly id: string }>(a: T, b: T): number =>
  a.id < b.id ? -1 : a.id > b.id ? 1 : 0;

export function ownHeroes(obs: PlayerObservation): readonly ObservedHero[] {
  const self = obs.self;
  if (self === null) return [];
  return obs.heroes.filter((h) => h.owner === self.id).slice().sort(byId);
}

export function ownTowns(obs: PlayerObservation): readonly ObservedTown[] {
  const self = obs.self;
  if (self === null) return [];
  return obs.towns.filter((t) => t.owner === self.id).slice().sort(byId);
}

export const findHero = (obs: PlayerObservation, id: HeroId | null): ObservedHero | null =>
  id === null ? null : obs.heroes.find((h) => h.id === id) ?? null;

export const findTown = (obs: PlayerObservation, id: TownId | null): ObservedTown | null =>
  id === null ? null : obs.towns.find((t) => t.id === id) ?? null;

export const findObject = (obs: PlayerObservation, id: ObjectId | null): ObservedObject | null =>
  id === null ? null : obs.objects.find((o) => o.id === id) ?? null;

export const heroAt = (obs: PlayerObservation, x: number, y: number, z: number): ObservedHero | null =>
  obs.heroes.find((h) => h.x === x && h.y === y && h.z === z) ?? null;

export const townAt = (obs: PlayerObservation, x: number, y: number, z: number): ObservedTown | null =>
  obs.towns.find((t) => t.x === x && t.y === y && t.z === z) ?? null;

export const objectAt = (obs: PlayerObservation, x: number, y: number, z: number): ObservedObject | null =>
  obs.objects.find((o) => o.x === x && o.y === y && o.z === z) ?? null;

/** The hero's army, or null for an enemy hero (whose sheet is never observed). */
export const armyOf = (hero: ObservedHero | null): ArmyState | null => hero?.army ?? null;

/** Null on unexplored ground: the observation zeroes those cells, and painting
 *  index 0 there would show water everywhere the player has not been. */
export function terrainAt(obs: PlayerObservation, x: number, y: number, z: number): TerrainKind | null {
  const m = obs.map;
  if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) return null;
  const i = z * m.width * m.height + y * m.width + x;
  if ((m.visibility[i] ?? 0) === 0) return null;
  return TERRAINS[m.terrain[i] ?? 0] ?? null;
}

/**
 * What a press on a cell is about, in the order a player means it: the hero
 * standing there, else the town, else the object, else the ground. The same
 * reading the status plate gives a selected hero's own cell, made a function
 * so the phone's Info key - which has no pointer to pick with - lands on the
 * same card a right press would.
 */
export function infoTargetAt(obs: PlayerObservation, x: number, y: number, z: number): HoverTarget {
  const hero = obs.heroes.find((h) => h.x === x && h.y === y && h.z === z);
  if (hero !== undefined) return { kind: "HERO", id: hero.id };
  const town = townAt(obs, x, y, z);
  if (town !== null) return { kind: "TOWN", id: town.id };
  const object = objectAt(obs, x, y, z);
  if (object !== null) return { kind: "OBJECT", id: object.id };
  return { kind: "CELL", x, y, z };
}

/**
 * The magic ground under a cell - Ley Plains, Hexed Soil and the rest
 * (Section 31) - as its content id, or null on ordinary ground or where the
 * player has never looked. Read the same way `terrainAt` reads the ground
 * plane: the map carries a slot per cell and a legend of ids, slot 0 plain.
 */
export function magicGroundAt(obs: PlayerObservation, x: number, y: number, z: number): string | null {
  const m = obs.map;
  if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) return null;
  const i = z * m.width * m.height + y * m.width + x;
  if ((m.visibility[i] ?? 0) === 0) return null;
  const slot = m.ground[i] ?? 0;
  if (slot === 0) return null;
  return m.groundIndex[slot] ?? null;
}

/**
 * The next hero that still has movement, cycling from `after`. This is the
 * space-bar behaviour: it skips heroes that have spent their day, and returns
 * the current hero only when it is the sole one left with anything to do.
 */
export function nextHeroWithMovement(obs: PlayerObservation, after: HeroId | null): HeroId | null {
  const heroes = ownHeroes(obs);
  if (heroes.length === 0) return null;
  const start = after === null ? -1 : heroes.findIndex((h) => h.id === after);
  for (let step = 1; step <= heroes.length; step++) {
    const hero = heroes[(start + step + heroes.length) % heroes.length];
    if (hero === undefined) continue;
    const left = hero.detail?.movementLeft ?? 0;
    if (left > 0) return hero.id;
  }
  return heroes[0]?.id ?? null;
}

/** True when every hero has spent their movement, which is when the end-turn
 *  button should start glowing. */
export function allHeroesSpent(obs: PlayerObservation): boolean {
  const heroes = ownHeroes(obs);
  if (heroes.length === 0) return true;
  return heroes.every((h) => (h.detail?.movementLeft ?? 0) <= 0);
}
